import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import L from 'leaflet';
import { DRAW_MODE, getDrawLocalModel } from '../Model/Model';
import { MODEL_MAP_TYPE } from '../../Model/Model';
import HandleChange from '../../../../../Helpers/HandleChange';
import { getGeolocation } from '../../../../../Apis/functions';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../../../enums/colors';
/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */
const DefineAreaStepApp = ({
    editMode,
    latitude,
    setLatitude,
    longitude,
    setLongitude,
    item,
    setItem,
    setGeofences,
}) => {
    const [mapType, setMapType] = useState(MODEL_MAP_TYPE.MAP);
    const [drawMode, setDrawMode] = useState(null);
    const [hasShapes, setHasShapes] = useState(false);
    const featureGroupRef = useRef(null);
    const mapRef = useRef(null);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    const cleanMap = () =>{
        featureGroupRef.current.clearLayers();
        setHasShapes(false);
        setGeofences([]);
    }


    /** Tile URLs for different map types */
    const tileUrls = {
        map: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    };

    useEffect(() => {
        getGeolocation({ setLatitude, setLongitude });
    }, []);

    /**
     * Component to handle drawing shapes on the map using Leaflet Draw
     *
     * @param {Object} params - Parameters
     * @param {string|null} params.drawMode - Current drawing mode ('polygon' or 'rectangle')
     * @param {React.Ref} params.featureGroupRef - Ref to feature group layer

     */

    const DrawHandler = ({
         drawMode,
         featureGroupRef,
         geofences,
         setGeofences,
         setDrawMode,
         setLatitude,
         setLongitude,
         itemData,
         handleChange
    }) => {
        const map = useMap();
        const [editingLayer, setEditingLayer] = useState(null);
        const { t } = useTranslation();

        useEffect(() => {
            L.drawLocal = getDrawLocalModel(t);
        }, [t]);
        /** Update hasShapes flag whenever a layer is added or removed */
        useEffect(() => {
            if (!featureGroupRef.current) return;

            const updateHasShapes = () => {
                const hasLayers = featureGroupRef.current.getLayers().length > 0;
                setHasShapes(hasLayers);
            };

            updateHasShapes();

            featureGroupRef.current.on('layeradd', updateHasShapes);
            featureGroupRef.current.on('layerremove', updateHasShapes);

            return () => {
                if (featureGroupRef.current) {
                    featureGroupRef.current.off('layeradd', updateHasShapes);
                    featureGroupRef.current.off('layerremove', updateHasShapes);
                }
            };
        }, [featureGroupRef]);

        /** Render existing geofence on map */
        useEffect(() => {
            if (geofences.length === 0) return;

            featureGroupRef.current.clearLayers();
            const element = geofences[0];

            let layer;
            const options = {
                color: element.strokeColor,
                weight: Number(element.strokeWidth),
                fillColor: element.fillColor,
                fillOpacity: 0.4,
            };

            if (element.type ===  DRAW_MODE.POLYGON ) layer = L.polygon(element.coordinates, options);
            else if (element.type === DRAW_MODE.RECTANGLE) {
                const lats = element.coordinates.map((c) => c[0]);
                const lngs = element.coordinates.map((c) => c[1]);
                const southWest = [Math.min(...lats), Math.min(...lngs)];
                const northEast = [Math.max(...lats), Math.max(...lngs)];
                layer = L.rectangle([southWest, northEast], options);
            }
            layer?.on('click', () => {
                handleChange({ target: { name: "strokeColor", value: element.strokeColor}})
                handleChange({ target: { name: "fillColor", value: element.fillColor}})
                handleChange({ target: { name: "strokeWidth", value: element.strokeWidth}})
                setDrawMode(element.type);
                setEditingLayer(layer);
            });

            if (layer) featureGroupRef.current.addLayer(layer);

        }, [geofences]);

        /** Update style of currently editing layer */
        useEffect(() => {
            if(!editingLayer) return;

            editingLayer.setStyle({
                color: itemData.strokeColor,
                fillColor: itemData.fillColor,
                weight: Number(itemData.strokeWidth),
                fillOpacity: 0.4,
            });
        }, [itemData.strokeColor, itemData.fillColor, itemData.strokeWidth, editingLayer]);

        /** Handle enabling Leaflet draw and creating new shapes */
        useEffect(() => {
            if (!map) return;
            if (map.drawHandler) {
                map.drawHandler.disable();
                map.drawHandler = null;
            }
            if (!drawMode) return;

            const commonStyle = {
                color: itemData.strokeColor,
                weight: Number(itemData.strokeWidth),
                opacity: 1,
                fillColor: itemData.fillColor,
                fillOpacity: 0.4,
            };

            let drawHandler;
            if (drawMode === DRAW_MODE.POLYGON) drawHandler = new L.Draw.Polygon(map, { shapeOptions: commonStyle });
            if (drawMode === DRAW_MODE.RECTANGLE) drawHandler = new L.Draw.Rectangle(map, { shapeOptions: commonStyle });

            if (drawHandler) {
                map.drawHandler = drawHandler;
                drawHandler.enable();
            }
            // When drawing a polygon, changes first point style to a square with custom color for better visibility.
            let firstMarker = null;
            map.on('draw:drawvertex', (e) => {
                if (!firstMarker && drawMode === DRAW_MODE.POLYGON) {
                    const layers = e.layers.getLayers();
                    if (!firstMarker && layers.length > 0) {
                        firstMarker = layers[0];
                        const markerEl = firstMarker._icon || firstMarker._path || firstMarker._renderer?._container;

                        if (markerEl) {
                            markerEl.style.background = colors.shades700;
                            markerEl.style.border = colors.general950;
                        }
                    }
                }
            });

            /** Event handler for creating new shapes */
            const handleCreate = (e) => {
                if (firstMarker) {
                    firstMarker = null;
                }
                const layer = e.layer;
                featureGroupRef.current.addLayer(layer);
                setDrawMode(null);

                let coordinates;
                if (drawMode === DRAW_MODE.RECTANGLE) {
                    const bounds = layer.getBounds();
                    coordinates = [
                        [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
                        [bounds.getNorthWest().lat, bounds.getNorthWest().lng],
                        [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
                        [bounds.getSouthEast().lat, bounds.getSouthEast().lng],
                    ];
                } else {
                    coordinates = layer.getLatLngs()[0].map((p) => [p.lat, p.lng]);
                }

                let center;
                if (drawMode === DRAW_MODE.RECTANGLE) {
                    const bounds = layer.getBounds();
                    center = bounds.getCenter();
                } else if (drawMode === DRAW_MODE.POLYGON) {
                    center = layer.getBounds().getCenter();
                }

                if (center) {
                    setLatitude(center.lat);
                    setLongitude(center.lng);
                }

                if (editMode && geofences.length > 0) {
                    setGeofences([{ ...geofences[0], coordinates, strokeColor: itemData.strokeColor, fillColor: itemData.fillColor, strokeWidth: itemData.strokeWidth, type: drawMode }]);
                } else {
                    const newGeofence = {
                        id: Date.now(),
                        type: drawMode,
                        strokeColor: itemData.strokeColor,
                        fillColor: itemData.fillColor,
                        strokeWidth: itemData.strokeWidth,
                        coordinates,
                    };
                    featureGroupRef.current.clearLayers();
                    featureGroupRef.current.addLayer(e.layer);

                    setGeofences([newGeofence]);
                }
            };

            map.on(L.Draw.Event.CREATED, handleCreate);

            return () => {
                map.off(L.Draw.Event.CREATED, handleCreate);
                if (drawHandler) drawHandler.disable();
            };
        }, [drawMode, itemData.strokeColor, itemData.fillColor, itemData.strokeWidth, featureGroupRef, geofences,]);

        return null;
    };


    return {
        handleChange,
        drawMode,
        setDrawMode,
        mapType,
        tileUrls,
        featureGroupRef,
        DrawHandler,
        hasShapes,
        mapRef,
        latitude,
        longitude,
        cleanMap
    };
};

export default DefineAreaStepApp;
